import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { customersAPI, API_BASE_URL } from '../services/api';
import { useDraft, DraftRestoreController } from '../contexts/DraftContext';
import DraftNotice from '../components/DraftNotice';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import AddPortalUserModal from '../components/Portal/AddPortalUserModal';
import ResetPasswordModal from '../components/Portal/ResetPasswordModal';
import CustomerFeedAvailability from '../components/CustomerFeedAvailability';
import CustomerTaxExemption from '../components/CustomerTaxExemption';
import CustomerShippingPreferences from '../components/CustomerShippingPreferences';
import CustomerStoreLogo from '../components/CustomerStoreLogo';
import CustomerPublicInfo from '../components/CustomerPublicInfo';
import CustomerLocationsTab from '../components/CustomerLocationsTab';
import CustomerCreationModal from '../components/CustomerCreationModal';
import CustomerDocumentsTab from '../components/CustomerDocumentsTab';
import CustomerCreditsTab from './Customers/CustomerCreditsTab';
import CustomerBillingPanel from '../components/CustomerBillingPanel';
import { MobileCard } from '../components/MobileCard';
import '../components/MobileCard.css';
import './Customers.css';

interface PortalUser {
  id: number;
  user_email: string;
  user_name: string;
  user_first_name: string;
  user_last_name: string;
  is_portal_active: boolean;
  role: 'owner' | 'staff' | 'cashier' | 'viewer';
  can_place_orders: boolean;
  can_view_invoices: boolean;
  can_access_bridge_club: boolean;
  can_manage_bridge_club_members: boolean;
  can_redeem_rewards: boolean;
  can_view_store_credits: boolean;
  farm_role: string | null;
  can_manage_farm_employees: boolean;
  can_create_tasks: boolean;
  can_complete_tasks: boolean;
  can_edit_farm_map: boolean;
  can_log_movements: boolean;
  created_at: string;
  last_login: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner/Manager',
  staff: 'Staff',
  cashier: 'Cashier',
  viewer: 'Viewer',
};

interface Customer {
  id: number;
  name: string;
  customer_type: string;
  city: string;
  state: string;
  postal_code: string;
  address: string;
  phone: string;
  email: string;
  contact_person: string;
  notes: string;
  is_active: boolean;
  can_have_portal: boolean;
  portal_users: PortalUser[];
  // Payment terms
  payment_terms: string;
  payment_terms_start: string;
  payment_terms_display: string;
  credit_limit: number | null;
  current_balance: number;
  is_over_credit_limit: boolean;
  // Milk run fields
  milk_run_capable: boolean;
  milk_run_region: string;
  milk_run_offload_equipment: string;
  milk_run_truck_access: boolean;
  milk_run_truck_access_notes: string;
  milk_run_notes: string;
  latitude: number | null;
  longitude: number | null;
  // Store logo for Bridge Club co-branding
  store_logo: string | null;
  store_logo_background_color: string;
  // AR snapshot — from feedmill.customer_ar.get_customer_ar_data
  open_invoices?: Array<{
    id: number;
    invoice_number: string;
    invoice_date: string | null;
    due_date: string | null;
    total_amount: string;
    balance_due: string;
    status: string;
    is_overdue: boolean;
    order_number?: string | null;
  }>;
  recent_payments?: Array<{
    id: number;
    amount: string;
    payment_date: string | null;
    payment_method: string;
    invoice_number?: string | null;
    transaction_id?: string | null;
  }>;
  ar_summary?: {
    total_outstanding: string;
    overdue_count: number;
    overdue_amount: string;
  };
}

const Customers: React.FC = () => {
  // Deep link support: /customers/:id auto-opens the modal for that customer.
  // ?tab=billing (or any other tab name) auto-selects a tab on open.
  const { id: urlCustomerId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Skip/exclude summary from the RingCentral export, surfaced after download (not silent).
  const [ringcentralInfo, setRingcentralInfo] = useState('');
  // Server-side search: the input binds to `searchInput` (instant), while the debounced
  // `searchTerm` (400ms for a network call) is what loadCustomers queries on. `flushSearch`
  // commits immediately on Enter so the user isn't made to wait out the debounce.
  // Filter/search/page state is seeded FROM the URL so a refresh or back-navigation
  // restores it (see the URL-sync effect below).
  const { value: searchInput, setValue: setSearchInput, debounced: searchTerm, flush: flushSearch } =
    useDebouncedSearch(searchParams.get('q') || '', 400);
  const [selectedType, setSelectedType] = useState(() => searchParams.get('type') || '');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  // Portal user management state
  const [showAddPortalUserModal, setShowAddPortalUserModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedPortalUser, setSelectedPortalUser] = useState<PortalUser | null>(null);

  // Edit portal user (email + name) modal state
  const [editPortalUser, setEditPortalUser] = useState<PortalUser | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editSendSetupEmail, setEditSendSetupEmail] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Tab state for customer detail modal
  const [activeTab, setActiveTab] = useState<'details' | 'portal' | 'feeds' | 'tax' | 'milkrun' | 'logo' | 'public' | 'locations' | 'docs' | 'billing' | 'credits'>('details');

  // Customer creation modal state
  const [showCustomerCreationModal, setShowCustomerCreationModal] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Customer>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Draft persistence: in-progress customer edits survive in-tab navigation.
  const { hasDraft, getDraft, setDraftField, setBaseline, clearDraft } = useDraft();
  const [draftMode, setDraftMode] = useState<'none' | 'restored' | 'conflict'>('none');
  const custDraftKey = selectedCustomer ? `customer:${selectedCustomer.id}` : '';
  const SENSITIVE_CUST_FIELDS: Record<string, string> = { credit_limit: 'Credit limit' };
  // The field set the edit form manages, seeded from a customer record.
  const seedFromCustomer = (c: Customer): Partial<Customer> => ({
    name: c.name, customer_type: c.customer_type, is_active: c.is_active,
    contact_person: c.contact_person || '', phone: c.phone || '', email: c.email || '',
    address: c.address || '', city: c.city || '', state: c.state || '',
    postal_code: c.postal_code || '', payment_terms: c.payment_terms || 'due_on_receipt',
    credit_limit: c.credit_limit, notes: c.notes || '',
  });

  // Delete/deactivate state
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);
  const [deleteModalMode, setDeleteModalMode] = useState<'confirm' | 'has-orders'>('confirm');
  const [deleteOrderCount, setDeleteOrderCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Classification change confirmation
  const [showClassificationConfirm, setShowClassificationConfirm] = useState(false);

  const customerTypes = [
    { value: '', label: 'All Types' },
    { value: 'WHOLESALE_FARM', label: 'Wholesale Farm' },
    { value: 'RETAIL_PARTNER', label: 'Retail Partner' },
    { value: 'LOCAL_WILL_CALL', label: 'Local Will Call' },
    { value: 'DIRECT_CONSUMER', label: 'Direct Consumer' },
    { value: 'AMAZON', label: 'Amazon' },
  ];

  // Customer type options for edit dropdown
  const editableCustomerTypes = [
    { value: 'WHOLESALE_FARM', label: 'Wholesale Farm' },
    { value: 'RETAIL_PARTNER', label: 'Retail Partner' },
    { value: 'LOCAL_WILL_CALL', label: 'Local Will Call' },
    { value: 'DIRECT_CONSUMER', label: 'Direct Consumer' },
    { value: 'AMAZON', label: 'Amazon' },
    { value: 'OTHER', label: 'Other' },
  ];

  // Payment terms options
  const paymentTermsOptions = [
    { value: 'due_on_receipt', label: 'Due on Receipt' },
    { value: 'net_10', label: 'Net 10' },
    { value: 'net_15', label: 'Net 15' },
    { value: 'net_30', label: 'Net 30' },
    { value: 'net_45', label: 'Net 45' },
    { value: 'net_60', label: 'Net 60' },
  ];

  // US States for dropdown
  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  // Edit mode handlers
  const handleEnterEditMode = () => {
    if (selectedCustomer) {
      const key = `customer:${selectedCustomer.id}`;
      const base = seedFromCustomer(selectedCustomer);
      if (hasDraft(key)) {
        // A retained draft (edited earlier, navigated away, came back) — restore it
        // on top of the current record, and flag whether the record changed since.
        const d = getDraft(key)!;
        setEditFormData({ ...base, ...d.fields });
        setHasUnsavedChanges(true);
        const serverVersion = (selectedCustomer as any).updated_at ?? null;
        setDraftMode(d.baseline == null || serverVersion == null || d.baseline === String(serverVersion)
          ? 'restored' : 'conflict');
      } else {
        setEditFormData(base);
        setHasUnsavedChanges(false);
        setBaseline(key, (selectedCustomer as any).updated_at ?? null);
        setDraftMode('none');
      }
      setIsEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return;
      }
    }
    if (custDraftKey) clearDraft(custDraftKey);   // explicit cancel discards the draft
    setIsEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setDraftMode('none');
  };

  const handleEditFieldChange = (field: string, value: string | number | boolean | null) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    if (custDraftKey) setDraftField(custDraftKey, field, value, { sensitive: field in SENSITIVE_CUST_FIELDS });
  };

  // Restore banner / stale-record choice for the customer edit form.
  const custDraft = custDraftKey ? getDraft(custDraftKey) : undefined;
  const custDraftController: DraftRestoreController = {
    mode: draftMode,
    createdAt: custDraft ? custDraft.createdAt : null,
    changedFields: custDraft ? Object.keys(custDraft.fields) : [],
    sensitiveLabels: custDraft ? custDraft.touchedSensitive.map(f => SENSITIVE_CUST_FIELDS[f] || f) : [],
    dismiss: () => setDraftMode('none'),
    keepMine: () => {
      if (custDraftKey && selectedCustomer) setBaseline(custDraftKey, (selectedCustomer as any).updated_at ?? null);
      setDraftMode('none');
    },
    discard: () => {
      if (custDraftKey) clearDraft(custDraftKey);
      if (selectedCustomer) setEditFormData(seedFromCustomer(selectedCustomer));
      setHasUnsavedChanges(false);
      setDraftMode('none');
    },
  };

  const handleSaveCustomer = async () => {
    if (!selectedCustomer) return;

    // Check if classification changed — require confirmation
    const classificationChanged = editFormData.customer_type &&
      editFormData.customer_type !== selectedCustomer.customer_type;
    if (classificationChanged && !showClassificationConfirm) {
      setShowClassificationConfirm(true);
      return;
    }

    setShowClassificationConfirm(false);
    setIsSaving(true);
    try {
      // If classification changed, use the dedicated endpoint first
      if (classificationChanged) {
        await customersAPI.upgradeClassification(selectedCustomer.id, editFormData.customer_type!);
      }

      // Save all other fields via normal PUT
      const response = await customersAPI.update(selectedCustomer.id, editFormData);
      const updatedCustomer = response.data;

      // Update local state
      setSelectedCustomer(updatedCustomer);
      setCustomers(prev =>
        prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
      );

      if (selectedCustomer) clearDraft(`customer:${selectedCustomer.id}`);   // saved — draft done
      setIsEditMode(false);
      setEditFormData({});
      setHasUnsavedChanges(false);
      setDraftMode('none');

      const msg = classificationChanged
        ? `Customer updated! Classification changed to ${editableCustomerTypes.find(t => t.value === editFormData.customer_type)?.label || editFormData.customer_type}.`
        : 'Customer updated successfully!';
      alert(msg);
    } catch (err: any) {
      console.error('Full error object:', err);
      console.error('Response data:', err.response?.data);
      console.error('Response status:', err.response?.status);

      // Show the ACTUAL error from the server
      let errorMsg = 'Failed to save customer';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        } else {
          // Show all field errors
          errorMsg = Object.entries(err.response.data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('\n');
        }
      }
      alert(`Error: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    setSelectedCustomer(null);
    setIsEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setActiveTab('details');
    // If we got here via /customers/:id, drop the id from the URL — but KEEP the
    // query string so any active filters/search survive closing the modal.
    if (urlCustomerId) {
      navigate({ pathname: '/customers', search: searchParams.toString() });
    }
  };

  // /customers/:id deep link → open the modal for that customer and (optionally)
  // pre-select the tab from ?tab=. Runs whenever the URL or the loaded
  // customers list changes. Two phases: open from cache if we already have the
  // row, otherwise fetch the single customer directly.
  useEffect(() => {
    if (!urlCustomerId) return;
    const numericId = parseInt(urlCustomerId, 10);
    if (Number.isNaN(numericId)) return;
    if (selectedCustomer?.id === numericId) return; // already open

    const cached = customers.find((c) => c.id === numericId);
    if (cached) {
      setSelectedCustomer(cached);
      return;
    }
    // Not in the current page of the list — fetch directly.
    customersAPI.getById(numericId)
      .then((res) => setSelectedCustomer(res.data))
      .catch(() => {
        // Customer not found or no permission; leave the URL alone but don't
        // crash. The user will see the empty list state.
      });
  }, [urlCustomerId, customers, selectedCustomer?.id]);

  // Auto-select tab from ?tab= when the modal opens.
  useEffect(() => {
    if (!selectedCustomer || !urlTab) return;
    const validTabs = ['details', 'portal', 'feeds', 'tax', 'milkrun', 'logo', 'public', 'locations', 'docs', 'billing', 'credits'] as const;
    if ((validTabs as readonly string[]).includes(urlTab)) {
      setActiveTab(urlTab as typeof validTabs[number]);
    }
  }, [selectedCustomer?.id, urlTab]);

  // Delete/deactivate handlers
  const handleDeleteClick = (customer: Customer, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(customer);
    setDeleteModalMode('confirm');
    setDeleteOrderCount(0);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await customersAPI.delete(deleteTarget.id);
      setCustomers(prev => prev.filter(c => c.id !== deleteTarget.id));
      if (selectedCustomer?.id === deleteTarget.id) {
        setSelectedCustomer(null);
      }
      setDeleteTarget(null);
      alert(`Customer "${deleteTarget.name}" has been permanently deleted.`);
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.has_orders) {
        setDeleteModalMode('has-orders');
        setDeleteOrderCount(err.response.data.order_count || 0);
      } else {
        alert(`Error deleting customer: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const response = await customersAPI.deactivate(deleteTarget.id);
      const updated = response.data;
      setCustomers(prev => prev.map(c => c.id === updated.id ? updated : c));
      if (selectedCustomer?.id === updated.id) {
        setSelectedCustomer(updated);
      }
      setDeleteTarget(null);
      alert(`Customer "${updated.name}" has been deactivated.`);
    } catch (err: any) {
      alert(`Error deactivating customer: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReactivate = async (customer: Customer, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await customersAPI.reactivate(customer.id);
      const updated = response.data;
      setCustomers(prev => prev.map(c => c.id === updated.id ? updated : c));
      if (selectedCustomer?.id === updated.id) {
        setSelectedCustomer(updated);
      }
      alert(`Customer "${updated.name}" has been reactivated.`);
    } catch (err: any) {
      alert(`Error reactivating customer: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Load customers when the debounced search, page, or type filter changes.
  // (Page reset on search change is handled in the input's onChange/Enter so the
  // reset and the query stay in step and don't trigger a double fetch.)
  useEffect(() => {
    loadCustomers();
  }, [currentPage, searchTerm, selectedType]);

  // Mirror filter/search/page into the URL so refresh + back-navigation restore them.
  // Keyed on the DEBOUNCED search (searchTerm), so the URL updates once per pause, not
  // per keystroke. `replace: true` means filtering doesn't stack a history entry —
  // browser-back goes to the previous page, not through each filter change. The
  // functional updater preserves any other params (e.g. ?tab= used by the modal) and
  // leaves the /customers/:id path untouched, so opening the modal never wipes filters.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (searchTerm) next.set('q', searchTerm); else next.delete('q');
      if (selectedType) next.set('type', selectedType); else next.delete('type');
      if (currentPage > 1) next.set('page', String(currentPage)); else next.delete('page');
      return next;
    }, { replace: true });
  }, [searchTerm, selectedType, currentPage, setSearchParams]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await customersAPI.getAll({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
        customer_type: selectedType || undefined,
      });

      // Handle paginated response
      if (response.data.results) {
        setCustomers(response.data.results);
        setTotalCount(response.data.count || 0);
        setHasNextPage(!!response.data.next);
        setHasPrevPage(!!response.data.previous);
      } else {
        // Non-paginated response fallback
        setCustomers(response.data);
        setTotalCount(response.data.length);
        setHasNextPage(false);
        setHasPrevPage(false);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change - reset to page 1
  const handleTypeFilterChange = (type: string) => {
    setSelectedType(type);
    setCurrentPage(1);
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const formatCustomerType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '-';
    // Simple format: (XXX) XXX-XXXX
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Portal user management functions
  const reloadCustomerDetails = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await customersAPI.getById(selectedCustomer.id);
      const updatedCustomer = response.data;
      setSelectedCustomer(updatedCustomer);

      // Update in the customers list as well
      setCustomers(prev =>
        prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
      );
    } catch (err) {
      console.error('Error reloading customer:', err);
    }
  };

  const handleAddPortalUserSuccess = () => {
    setShowAddPortalUserModal(false);
    reloadCustomerDetails();
  };

  const handleResetPasswordSuccess = () => {
    setShowResetPasswordModal(false);
    setSelectedPortalUser(null);
    reloadCustomerDetails();
  };

  const handleTogglePortalUser = async (portalUser: PortalUser) => {
    if (!selectedCustomer) return;

    const action = portalUser.is_portal_active ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} portal access for ${portalUser.user_email}?`)) {
      return;
    }

    try {
      await fetch(
        `${API_BASE_URL}/customers/${selectedCustomer.id}/portal_users/${portalUser.id}/toggle_active/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      reloadCustomerDetails();
    } catch (err) {
      console.error('Error toggling portal user:', err);
      alert('Failed to update portal user status');
    }
  };

  const handleDeletePortalUser = async (portalUser: PortalUser) => {
    if (!selectedCustomer) return;

    if (!window.confirm(
      `Are you sure you want to remove portal access for ${portalUser.user_email}?\n\nThis action cannot be undone.`
    )) {
      return;
    }

    try {
      await fetch(
        `${API_BASE_URL}/customers/${selectedCustomer.id}/portal_users/${portalUser.id}/`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      reloadCustomerDetails();
    } catch (err) {
      console.error('Error deleting portal user:', err);
      alert('Failed to remove portal user');
    }
  };

  const openEditPortalUser = (portalUser: PortalUser) => {
    setEditPortalUser(portalUser);
    setEditEmail(portalUser.user_email || '');
    setEditFirstName(portalUser.user_first_name || '');
    setEditLastName(portalUser.user_last_name || '');
    setEditSendSetupEmail(false);
    setEditError('');
  };

  const handleSaveEditPortalUser = async () => {
    if (!selectedCustomer || !editPortalUser) return;
    setEditError('');
    setEditSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/customers/${selectedCustomer.id}/portal_users/${editPortalUser.id}/details/`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: editEmail.trim(),
            first_name: editFirstName.trim(),
            last_name: editLastName.trim(),
            send_setup_email: editSendSetupEmail,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEditError(data.error || 'Failed to update portal user.');
        return;
      }
      setEditPortalUser(null);
      reloadCustomerDetails();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update portal user.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleUpdatePermissions = async (portalUser: PortalUser, field: string, value: boolean | string) => {
    if (!selectedCustomer) return;

    try {
      await fetch(
        `${API_BASE_URL}/customers/${selectedCustomer.id}/portal_users/${portalUser.id}/permissions/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            [field]: value,
          }),
        }
      );
      reloadCustomerDetails();
    } catch (err) {
      console.error('Error updating permissions:', err);
      alert('Failed to update permissions');
    }
  };

  const handleRoleChange = async (portalUser: PortalUser, newRole: string) => {
    if (!selectedCustomer) return;

    try {
      await fetch(
        `${API_BASE_URL}/customers/${selectedCustomer.id}/portal_users/${portalUser.id}/permissions/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            role: newRole,
            apply_preset: true,  // Backend should apply role preset permissions
          }),
        }
      );
      reloadCustomerDetails();
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role');
    }
  };

  const handleFarmRoleChange = async (portalUser: PortalUser, newFarmRole: string) => {
    if (!selectedCustomer) return;

    try {
      await fetch(
        `${API_BASE_URL}/customers/${selectedCustomer.id}/portal_users/${portalUser.id}/permissions/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            farm_role: newFarmRole,
            apply_farm_preset: true,
          }),
        }
      );
      reloadCustomerDetails();
    } catch (err) {
      console.error('Error updating farm role:', err);
      alert('Failed to update farm role');
    }
  };

  const handleCustomerCreated = (newCustomer: Customer) => {
    setShowCustomerCreationModal(false);
    loadCustomers();
  };

  // Full-page spinner ONLY on the very first load. During a search/filter refetch the
  // list already has rows, so we keep the whole page (search input included) mounted —
  // the input never loses focus and the table shows the prior rows until new ones land.
  // This is what makes search feel like Store Inventory instead of reloading the page.
  if (loading && customers.length === 0) {
    return (
      <div className="customers-page">
        <div className="loading">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="customers-page">
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1>Customers</h1>
            <p className="page-subtitle">Red Bridge Farm Feed Co.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  const response = await customersAPI.exportCSV();
                  const blob = new Blob([response.data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'customers_export.csv';
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('CSV export failed:', err);
                }
              }}
            >
              Export CSV
            </button>
            <button
              className="btn btn-primary btn-new-customer"
              onClick={() => setShowCustomerCreationModal(true)}
            >
              + New Customer
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="customers-container">
        {/* Filters */}
        <div className="filters-section">
          <div className="filter-row">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search customers..."
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    flushSearch();
                    setCurrentPage(1);
                  }
                }}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <select
                value={selectedType}
                onChange={(e) => handleTypeFilterChange(e.target.value)}
                className="filter-select"
              >
                {customerTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="results-count">
              {totalCount > 0 ? (
                <>
                  Showing {startIndex}-{endIndex} of {totalCount} customer{totalCount !== 1 ? 's' : ''}
                </>
              ) : (
                '0 customers'
              )}
            </div>
            <button
              className="btn btn-secondary"
              onClick={async () => {
                setRingcentralInfo('');
                try {
                  const response = await customersAPI.exportRingCentral();
                  const blob = new Blob([response.data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const cd = (response.headers['content-disposition'] as string) || '';
                  const match = cd.match(/filename="?([^"]+)"?/);
                  a.download = match ? match[1] : 'red_bridge_ringcentral.csv';
                  a.click();
                  window.URL.revokeObjectURL(url);
                  const summary = response.headers['x-ringcentral-export-summary'] as string;
                  setRingcentralInfo(summary || 'RingCentral CSV downloaded.');
                } catch (err) {
                  console.error('RingCentral export failed:', err);
                  setRingcentralInfo('RingCentral export failed. Please try again.');
                }
              }}
            >
              Export for RingCentral
            </button>
          </div>
          {ringcentralInfo && (
            <div className="alert alert-info" onClick={() => setRingcentralInfo('')} role="status">
              {ringcentralInfo}
            </div>
          )}
        </div>

        {/* Customers Table */}
        <div className="desktop-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Terms</th>
                <th>City</th>
                <th>State</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-data">
                    {loading ? 'Loading...' : 'No customers found'}
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={`customer-row${!customer.is_active ? ' customer-inactive' : ''}`}
                    onClick={() => setSelectedCustomer(customer)}
                  >
                    <td className="customer-name">
                      {customer.name}
                      {!customer.is_active && <span className="badge-inactive">Inactive</span>}
                    </td>
                    <td>{formatCustomerType(customer.customer_type)}</td>
                    <td>
                      <span className={`payment-terms-pill ${customer.payment_terms !== 'due_on_receipt' ? 'has-terms' : ''}`}>
                        {customer.payment_terms_display || 'Due on Receipt'}
                      </span>
                    </td>
                    <td>{customer.city || '-'}</td>
                    <td>{customer.state || '-'}</td>
                    <td>{formatPhone(customer.phone)}</td>
                    <td className="customer-actions-cell">
                      <button
                        className="btn-view"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(customer);
                        }}
                      >
                        View
                      </button>
                      {customer.is_active ? (
                        <button
                          className="btn-delete-sm"
                          onClick={(e) => handleDeleteClick(customer, e)}
                        >
                          Delete
                        </button>
                      ) : (
                        <button
                          className="btn-reactivate-sm"
                          onClick={(e) => handleReactivate(customer, e)}
                        >
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1 || loading}
            >
              First
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={!hasPrevPage || loading}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!hasNextPage || loading}
            >
              Next
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || loading}
            >
              Last
            </button>
          </div>
        )}

        {/* Mobile Cards (shown on small screens) */}
        <div className="mobile-cards-container">
          <div className="mobile-card-list">
            {customers.length === 0 ? (
              <div className="no-data-card">{loading ? 'Loading...' : 'No customers found'}</div>
            ) : (
              customers.map((customer) => (
                <MobileCard
                  key={customer.id}
                  title={customer.name}
                  subtitle={formatCustomerType(customer.customer_type)}
                  badge={
                    <span className={`payment-terms-pill ${customer.payment_terms !== 'due_on_receipt' ? 'has-terms' : ''}`}>
                      {customer.payment_terms_display || 'Due on Receipt'}
                    </span>
                  }
                  fields={[
                    { label: 'City', value: customer.city || '-' },
                    { label: 'State', value: customer.state || '-' },
                    { label: 'Phone', value: formatPhone(customer.phone) },
                  ]}
                  actions={
                    <button className="btn btn-primary btn-sm" onClick={() => setSelectedCustomer(customer)}>
                      View Details
                    </button>
                  }
                  onClick={() => setSelectedCustomer(customer)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Customer Full-Screen View - NOT a modal */}
      {selectedCustomer && (
        <div className="customer-fullscreen-view">
          {/* Header */}
          <div className="customer-view-header">
            <h1>
              {selectedCustomer.name}
              {!selectedCustomer.is_active && <span className="badge-inactive">Inactive</span>}
            </h1>
            <div className="customer-view-header-actions">
              {activeTab === 'details' && !isEditMode && (
                <>
                  <button className="btn-header-edit" onClick={handleEnterEditMode}>
                    Edit
                  </button>
                  {selectedCustomer.is_active ? (
                    <button
                      className="btn-header-delete"
                      onClick={(e) => handleDeleteClick(selectedCustomer, e)}
                    >
                      Delete
                    </button>
                  ) : (
                    <button
                      className="btn-header-reactivate"
                      onClick={(e) => handleReactivate(selectedCustomer, e)}
                    >
                      Reactivate
                    </button>
                  )}
                </>
              )}
              {activeTab === 'details' && isEditMode && (
                <>
                  <button className="btn-header-cancel" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button
                    className="btn-header-save"
                    onClick={handleSaveCustomer}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
              <button className="btn-header-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="customer-view-tabs">
            <button
              className={activeTab === 'details' ? 'active' : ''}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={activeTab === 'portal' ? 'active' : ''}
              onClick={() => setActiveTab('portal')}
            >
              Portal Access
            </button>
            <button
              className={activeTab === 'feeds' ? 'active' : ''}
              onClick={() => setActiveTab('feeds')}
            >
              Feed Availability
            </button>
            <button
              className={activeTab === 'tax' ? 'active' : ''}
              onClick={() => setActiveTab('tax')}
            >
              Tax Exemption
            </button>
            <button
              className={activeTab === 'milkrun' ? 'active' : ''}
              onClick={() => setActiveTab('milkrun')}
            >
              Shipping
            </button>
            <button
              className={activeTab === 'billing' ? 'active' : ''}
              onClick={() => setActiveTab('billing')}
            >
              Billing
            </button>
            <button
              className={activeTab === 'credits' ? 'active' : ''}
              onClick={() => setActiveTab('credits')}
            >
              Credits
            </button>
            {selectedCustomer.customer_type === 'RETAIL_PARTNER' && (
              <>
                <button
                  className={activeTab === 'locations' ? 'active' : ''}
                  onClick={() => setActiveTab('locations')}
                >
                  Locations
                </button>
                <button
                  className={activeTab === 'logo' ? 'active' : ''}
                  onClick={() => setActiveTab('logo')}
                >
                  Store Logo
                </button>
                <button
                  className={activeTab === 'public' ? 'active' : ''}
                  onClick={() => setActiveTab('public')}
                >
                  Public Info
                </button>
              </>
            )}
            {selectedCustomer.can_have_portal && (
              <button
                className={activeTab === 'docs' ? 'active' : ''}
                onClick={() => setActiveTab('docs')}
              >
                Documents
              </button>
            )}
          </div>

          {/* Scrollable Content */}
          <div className="customer-view-content">
              {/* Details Tab */}
              {activeTab === 'details' && (
                <>
                  {/* VIEW MODE */}
                  {!isEditMode && (
                    <>
                      <div className="detail-section">
                        <h3>Customer Information</h3>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <label>Type:</label>
                            <span>{formatCustomerType(selectedCustomer.customer_type)}</span>
                          </div>
                          <div className="detail-item">
                            <label>Status:</label>
                            <span className={selectedCustomer.is_active ? 'status-active' : 'status-inactive'}>
                              {selectedCustomer.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3>Contact Information</h3>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <label>Contact Person:</label>
                            <span>{selectedCustomer.contact_person || '-'}</span>
                          </div>
                          <div className="detail-item">
                            <label>Phone:</label>
                            <span>{formatPhone(selectedCustomer.phone)}</span>
                          </div>
                          <div className="detail-item">
                            <label>Email:</label>
                            <span>{selectedCustomer.email || '-'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3>Address</h3>
                        <div className="address-block">
                          {selectedCustomer.address && <div>{selectedCustomer.address}</div>}
                          <div>
                            {selectedCustomer.city}
                            {selectedCustomer.city && selectedCustomer.state && ', '}
                            {selectedCustomer.state} {selectedCustomer.postal_code}
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3>Payment Terms</h3>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <label>Terms:</label>
                            <span className="payment-terms-badge">
                              {selectedCustomer.payment_terms_display || 'Due on Receipt'}
                            </span>
                          </div>
                          <div className="detail-item">
                            <label>Terms Start:</label>
                            <span>
                              {selectedCustomer.payment_terms_start === 'on_order' && 'On Order Date'}
                              {selectedCustomer.payment_terms_start === 'on_ship' && 'On Ship Date'}
                              {selectedCustomer.payment_terms_start === 'on_delivery' && 'On Delivery Date'}
                              {!selectedCustomer.payment_terms_start && 'On Ship Date'}
                            </span>
                          </div>
                          <div className="detail-item">
                            <label>Credit Limit:</label>
                            <span>
                              {selectedCustomer.credit_limit !== null
                                ? `$${Number(selectedCustomer.credit_limit).toLocaleString()}`
                                : 'No limit'}
                            </span>
                          </div>
                          <div className="detail-item">
                            <label>Current Balance:</label>
                            <span className={selectedCustomer.is_over_credit_limit ? 'balance-over-limit' : ''}>
                              ${Number(selectedCustomer.current_balance || 0).toLocaleString()}
                              {selectedCustomer.is_over_credit_limit && (
                                <span className="over-limit-warning"> (Over limit!)</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {selectedCustomer.notes && (
                        <div className="detail-section">
                          <h3>Notes</h3>
                          <div className="notes-block">{selectedCustomer.notes}</div>
                        </div>
                      )}
                    </>
                  )}

                  {/* EDIT MODE */}
                  {isEditMode && (
                    <div className="edit-form">
                      <DraftNotice controller={custDraftController} />
                      <div className="detail-section">
                        <h3>Customer Information</h3>
                        <div className="edit-grid">
                          <div className="edit-field">
                            <label htmlFor="customer_type">Type</label>
                            <select
                              id="customer_type"
                              className="edit-input"
                              value={editFormData.customer_type || ''}
                              onChange={(e) => handleEditFieldChange('customer_type', e.target.value)}
                            >
                              {editableCustomerTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="edit-field">
                            <label htmlFor="is_active">Status</label>
                            <select
                              id="is_active"
                              className={`edit-input status-select ${editFormData.is_active ? 'status-active' : 'status-inactive'}`}
                              value={editFormData.is_active ? 'active' : 'inactive'}
                              onChange={(e) => handleEditFieldChange('is_active', e.target.value === 'active')}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3>Contact Information</h3>
                        <div className="edit-grid">
                          <div className="edit-field">
                            <label htmlFor="contact_person">Contact Person</label>
                            <input
                              type="text"
                              id="contact_person"
                              className="edit-input"
                              value={editFormData.contact_person || ''}
                              onChange={(e) => handleEditFieldChange('contact_person', e.target.value)}
                              placeholder="Contact name"
                            />
                          </div>
                          <div className="edit-field">
                            <label htmlFor="phone">Phone</label>
                            <input
                              type="tel"
                              id="phone"
                              className="edit-input"
                              value={editFormData.phone || ''}
                              onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                              placeholder="(509) 555-1234"
                            />
                          </div>
                          <div className="edit-field full-width">
                            <label htmlFor="email">Email</label>
                            <input
                              type="email"
                              id="email"
                              className="edit-input"
                              value={editFormData.email || ''}
                              onChange={(e) => handleEditFieldChange('email', e.target.value)}
                              placeholder="email@example.com"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3>Address</h3>
                        <div className="edit-grid">
                          <div className="edit-field full-width">
                            <label htmlFor="address">Street Address</label>
                            <input
                              type="text"
                              id="address"
                              className="edit-input"
                              value={editFormData.address || ''}
                              onChange={(e) => handleEditFieldChange('address', e.target.value)}
                              placeholder="123 Main St"
                            />
                          </div>
                          <div className="edit-field">
                            <label htmlFor="city">City</label>
                            <input
                              type="text"
                              id="city"
                              className="edit-input"
                              value={editFormData.city || ''}
                              onChange={(e) => handleEditFieldChange('city', e.target.value)}
                              placeholder="City"
                            />
                          </div>
                          <div className="edit-field">
                            <label htmlFor="state">State</label>
                            <select
                              id="state"
                              className="edit-input"
                              value={editFormData.state || ''}
                              onChange={(e) => handleEditFieldChange('state', e.target.value)}
                            >
                              <option value="">Select State</option>
                              {usStates.map((st) => (
                                <option key={st} value={st}>{st}</option>
                              ))}
                            </select>
                          </div>
                          <div className="edit-field">
                            <label htmlFor="postal_code">Zip Code</label>
                            <input
                              type="text"
                              id="postal_code"
                              className="edit-input"
                              value={editFormData.postal_code || ''}
                              onChange={(e) => handleEditFieldChange('postal_code', e.target.value)}
                              placeholder="99999"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3>Business Details</h3>
                        <div className="edit-grid">
                          <div className="edit-field">
                            <label htmlFor="payment_terms">Payment Terms</label>
                            <select
                              id="payment_terms"
                              className="edit-input"
                              value={editFormData.payment_terms || 'due_on_receipt'}
                              onChange={(e) => handleEditFieldChange('payment_terms', e.target.value)}
                            >
                              {paymentTermsOptions.map((term) => (
                                <option key={term.value} value={term.value}>
                                  {term.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="edit-field">
                            <label htmlFor="credit_limit">Credit Limit ($)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              id="credit_limit"
                              className="edit-input"
                              value={editFormData.credit_limit ?? ''}
                              onChange={(e) => handleEditFieldChange('credit_limit', e.target.value)}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val >= 0) {
                                  handleEditFieldChange('credit_limit', val);
                                } else {
                                  handleEditFieldChange('credit_limit', null);
                                }
                              }}
                              placeholder="No limit"
                            />
                          </div>
                          <div className="edit-field full-width">
                            <label htmlFor="notes">Notes</label>
                            <textarea
                              id="notes"
                              className="edit-input edit-textarea"
                              value={editFormData.notes || ''}
                              onChange={(e) => handleEditFieldChange('notes', e.target.value)}
                              placeholder="Customer notes..."
                              rows={4}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Portal Access Tab */}
              {activeTab === 'portal' && (
                <>
                  {selectedCustomer.can_have_portal ? (
                    <div className="detail-section portal-section">
                      <div className="section-header">
                        <h3>Portal Access</h3>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowAddPortalUserModal(true)}
                        >
                          + Add Portal User
                        </button>
                      </div>

                      {selectedCustomer.portal_users && selectedCustomer.portal_users.length > 0 ? (
                        <div className="portal-users-list">
                          {selectedCustomer.portal_users.map((portalUser) => (
                            <div key={portalUser.id} className="portal-user-card">
                              <div className="portal-user-header">
                                <div className="portal-user-info">
                                  <div className="portal-user-email">{portalUser.user_email}</div>
                                  {portalUser.user_name && (
                                    <div className="portal-user-name">{portalUser.user_name}</div>
                                  )}
                                </div>
                                <div className="portal-user-status">
                                  <span
                                    className={`status-badge ${
                                      portalUser.is_portal_active ? 'status-active' : 'status-inactive'
                                    }`}
                                  >
                                    {portalUser.is_portal_active ? 'Active' : 'Disabled'}
                                  </span>
                                </div>
                              </div>

                              <div className="portal-user-role">
                                <label className="role-label">Role:</label>
                                <select
                                  className="role-select"
                                  value={portalUser.role || 'staff'}
                                  onChange={(e) => handleRoleChange(portalUser, e.target.value)}
                                >
                                  <option value="owner">Owner/Manager - Full Access</option>
                                  <option value="staff">Staff - Orders & Bridge Club</option>
                                  <option value="cashier">Cashier - Bridge Club Only</option>
                                  <option value="viewer">Viewer - Read Only</option>
                                </select>
                              </div>

                              <div className="portal-user-meta">
                                <div className="meta-item">
                                  <strong>Last Login:</strong> {formatDate(portalUser.last_login)}
                                </div>
                                <div className="meta-item">
                                  <strong>Created:</strong>{' '}
                                  {new Date(portalUser.created_at).toLocaleDateString()}
                                </div>
                              </div>

                              <div className="portal-user-permissions">
                                <div className="permissions-group">
                                  <div className="permissions-group-header">Portal</div>
                                  <label className="permission-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={portalUser.can_place_orders}
                                      onChange={(e) =>
                                        handleUpdatePermissions(
                                          portalUser,
                                          'can_place_orders',
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <span>Can place orders</span>
                                  </label>
                                  <label className="permission-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={portalUser.can_view_invoices}
                                      onChange={(e) =>
                                        handleUpdatePermissions(
                                          portalUser,
                                          'can_view_invoices',
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <span>Can view invoices</span>
                                  </label>
                                </div>

                                {selectedCustomer.customer_type === 'RETAIL_PARTNER' && (
                                  <div className="permissions-group">
                                    <div className="permissions-group-header">Bridge Club</div>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_access_bridge_club}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_access_bridge_club',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Access Bridge Club</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_manage_bridge_club_members}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_manage_bridge_club_members',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Manage members</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_redeem_rewards}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_redeem_rewards',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Redeem rewards</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_view_store_credits}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_view_store_credits',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>View store credits</span>
                                    </label>
                                  </div>
                                )}

                                {selectedCustomer.customer_type === 'WHOLESALE_FARM' && (
                                  <div className="permissions-group">
                                    <div className="permissions-group-header">Farm Role</div>
                                    <div className="farm-role-select-wrapper">
                                      <select
                                        className="role-select"
                                        value={portalUser.farm_role || ''}
                                        onChange={(e) =>
                                          handleFarmRoleChange(portalUser, e.target.value)
                                        }
                                      >
                                        <option value="">-- Not Set --</option>
                                        <option value="farm_owner">
                                          Owner/Manager - Full Access
                                        </option>
                                        <option value="foreman">
                                          Foreman - Operations & Tasks
                                        </option>
                                        <option value="crew">
                                          Crew - View Tasks & Check In
                                        </option>
                                        <option value="farm_viewer">
                                          Viewer - Read Only
                                        </option>
                                      </select>
                                    </div>
                                  </div>
                                )}

                                {selectedCustomer.customer_type === 'WHOLESALE_FARM' && (
                                  <div className="permissions-group">
                                    <div className="permissions-group-header">
                                      Farm Permissions
                                    </div>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_manage_farm_employees}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_manage_farm_employees',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Manage farm employees</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_create_tasks}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_create_tasks',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Create tasks</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_complete_tasks}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_complete_tasks',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Complete tasks</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_edit_farm_map}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_edit_farm_map',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Edit farm map</span>
                                    </label>
                                    <label className="permission-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={portalUser.can_log_movements}
                                        onChange={(e) =>
                                          handleUpdatePermissions(
                                            portalUser,
                                            'can_log_movements',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <span>Log movements</span>
                                    </label>
                                  </div>
                                )}
                              </div>

                              <div className="portal-user-actions">
                                <button
                                  className="btn-action btn-edit"
                                  onClick={() => openEditPortalUser(portalUser)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn-action btn-reset"
                                  onClick={() => {
                                    setSelectedPortalUser(portalUser);
                                    setShowResetPasswordModal(true);
                                  }}
                                >
                                  Reset Password
                                </button>
                                <button
                                  className="btn-action btn-toggle"
                                  onClick={() => handleTogglePortalUser(portalUser)}
                                >
                                  {portalUser.is_portal_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  className="btn-action btn-delete"
                                  onClick={() => handleDeletePortalUser(portalUser)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-portal-users">
                          <p>No portal users created yet.</p>
                          <p className="text-muted">
                            Portal users can log in to view orders, quotes, and place new orders.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="detail-section">
                      <div className="info-message">
                        <strong>Portal Access Not Available</strong>
                        <p>
                          Only Wholesale Farm and Retail Partner customers can have portal access.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Feed Availability Tab */}
              {activeTab === 'feeds' && (
                <CustomerFeedAvailability
                  customerId={selectedCustomer.id}
                  customerType={selectedCustomer.customer_type}
                  customerName={selectedCustomer.name}
                />
              )}

              {/* Tax Exemption Tab */}
              {activeTab === 'tax' && (
                <CustomerTaxExemption
                  customerId={selectedCustomer.id}
                  customerName={selectedCustomer.name}
                />
              )}

              {/* Shipping Preferences Tab */}
              {activeTab === 'milkrun' && (
                <CustomerShippingPreferences
                  customer={selectedCustomer}
                  onUpdate={reloadCustomerDetails}
                />
              )}

              {/* Billing Tab — invoices, payments, AR summary (shared with CRM page) */}
              {activeTab === 'billing' && (
                <div style={{ padding: 12 }}>
                  <CustomerBillingPanel
                    invoices={selectedCustomer.open_invoices || []}
                    payments={selectedCustomer.recent_payments || []}
                    arSummary={selectedCustomer.ar_summary}
                    returnPath={`/customers/${selectedCustomer.id}?tab=billing`}
                  />
                </div>
              )}

              {/* Locations Tab (Retail Partners only) */}
              {activeTab === 'locations' && (
                <CustomerLocationsTab
                  customerId={selectedCustomer.id}
                  customerName={selectedCustomer.name}
                />
              )}

              {/* Store Logo Tab */}
              {activeTab === 'logo' && (
                <CustomerStoreLogo
                  customerId={selectedCustomer.id}
                  customerName={selectedCustomer.name}
                  customerType={selectedCustomer.customer_type}
                  currentLogoUrl={selectedCustomer.store_logo}
                  logoBackgroundColor={selectedCustomer.store_logo_background_color || '#FFFFFF'}
                  onUpdate={reloadCustomerDetails}
                />
              )}

              {/* Public Info Tab */}
              {activeTab === 'public' && (
                <CustomerPublicInfo
                  customerId={selectedCustomer.id}
                  customerName={selectedCustomer.name}
                  customerType={selectedCustomer.customer_type}
                  onUpdate={reloadCustomerDetails}
                />
              )}

              {/* Documents Tab */}
              {activeTab === 'docs' && (
                <CustomerDocumentsTab
                  customerId={selectedCustomer.id}
                  customerName={selectedCustomer.name}
                />
              )}

              {/* Credits Tab */}
              {activeTab === 'credits' && (
                <CustomerCreditsTab
                  customerId={selectedCustomer.id}
                  customerName={selectedCustomer.name}
                />
              )}
          </div>
        </div>
      )}

      {/* Portal User Modals */}
      {selectedCustomer && (
        <>
          <AddPortalUserModal
            isOpen={showAddPortalUserModal}
            onClose={() => setShowAddPortalUserModal(false)}
            customer={{
              id: selectedCustomer.id,
              name: selectedCustomer.name,
              email: selectedCustomer.email,
            }}
            onSuccess={handleAddPortalUserSuccess}
          />

          {selectedPortalUser && (
            <ResetPasswordModal
              isOpen={showResetPasswordModal}
              onClose={() => {
                setShowResetPasswordModal(false);
                setSelectedPortalUser(null);
              }}
              portalUser={{
                id: selectedPortalUser.id,
                user_email: selectedPortalUser.user_email,
              }}
              customerId={selectedCustomer.id}
              onSuccess={handleResetPasswordSuccess}
            />
          )}

          {editPortalUser && (
            <div
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => !editSaving && setEditPortalUser(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}
              >
                <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#111827' }}>Edit Portal User</h3>

                <div style={{ marginBottom: 12 }}>
                  <label htmlFor="edit-pu-email" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Email *</label>
                  <input
                    id="edit-pu-email" type="email" value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, minHeight: 44 }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ flex: '1 1 160px' }}>
                    <label htmlFor="edit-pu-first" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>First name</label>
                    <input
                      id="edit-pu-first" type="text" value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, minHeight: 44 }}
                    />
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <label htmlFor="edit-pu-last" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Last name</label>
                    <input
                      id="edit-pu-last" type="text" value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, minHeight: 44 }}
                    />
                  </div>
                </div>

                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#374151', marginBottom: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={editSendSetupEmail} onChange={(e) => setEditSendSetupEmail(e.target.checked)} style={{ marginTop: 3 }} />
                  <span>Send a password-setup / welcome email after saving (use this if the email was missing so the user can set a password and log in).</span>
                </label>

                {editError && (
                  <div role="alert" style={{ background: '#fef2f2', color: '#b91c1c', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                    {editError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setEditPortalUser(null)} disabled={editSaving} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, minHeight: 44, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button type="button" onClick={handleSaveEditPortalUser} disabled={editSaving || !editEmail.trim()} style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#2D5A27', color: '#fff', fontWeight: 600, minHeight: 44, cursor: (editSaving || !editEmail.trim()) ? 'not-allowed' : 'pointer', opacity: (editSaving || !editEmail.trim()) ? 0.6 : 1 }}>
                    {editSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Customer Creation Modal */}
      <CustomerCreationModal
        isOpen={showCustomerCreationModal}
        onClose={() => setShowCustomerCreationModal(false)}
        onCustomerCreated={handleCustomerCreated}
      />

      {/* Classification Change Confirmation Modal */}
      {showClassificationConfirm && selectedCustomer && (
        <div className="delete-modal-overlay" onClick={() => setShowClassificationConfirm(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Change Classification?</h3>
            <p>
              Change <strong>{selectedCustomer.name}</strong> from{' '}
              <strong>{editableCustomerTypes.find(t => t.value === selectedCustomer.customer_type)?.label || selectedCustomer.customer_type}</strong>{' '}
              to{' '}
              <strong>{editableCustomerTypes.find(t => t.value === editFormData.customer_type)?.label || editFormData.customer_type}</strong>?
            </p>
            <p>All order history, invoices, and data will be preserved. Portal access will be updated for the new classification.</p>
            <div className="delete-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowClassificationConfirm(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="btn-modal-confirm"
                onClick={handleSaveCustomer}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete/Deactivate Modal */}
      {deleteTarget && (
        <div className="delete-modal-overlay" onClick={() => !isDeleting && setDeleteTarget(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            {deleteModalMode === 'confirm' ? (
              <>
                <h3>Delete "{deleteTarget.name}"?</h3>
                <p>This action cannot be undone. The customer and all associated portal users will be permanently removed.</p>
                <div className="delete-modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setDeleteTarget(null)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-modal-delete"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Cannot Delete "{deleteTarget.name}"</h3>
                <p>
                  This customer has {deleteOrderCount} order{deleteOrderCount !== 1 ? 's' : ''} on file and cannot be deleted.
                  You can deactivate them instead — they won't appear in dropdowns or active customer lists.
                </p>
                <div className="delete-modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setDeleteTarget(null)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-modal-deactivate"
                    onClick={handleDeactivate}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deactivating...' : 'Deactivate'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
